import React, { useState, useEffect } from "react";
import StandardModal, { StandardModalBody, StandardModalFooter, useModalLoading } from '../../../components/modales/StandardModal';
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Rating from "@mui/material/Rating";
import Typography from "@mui/material/Typography";
import ReceiptLongOutlined from "@mui/icons-material/ReceiptLongOutlined";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import NotificationsActiveOutlined from "@mui/icons-material/NotificationsActiveOutlined";
import NotificationsOffOutlined from "@mui/icons-material/NotificationsOffOutlined";
import { EmptyState, StatusChip, type DataTableColumn } from "@routes-plan/ui";
import axios from "axios";
import moment from "moment-timezone";
import { nowOperationalDateString } from "../../../utils/operationalDateTime";
import { formatCurrency } from "../../../utils/format/currency";
import PermissionButton from "../../../components/common/PermissionButton";
import LoanProductsHistoryModal from "./LoanProductsHistoryModal";
import { useT, type TextKey } from "../../../i18n";
import ClientRatingPanel from "../../../components/rating/ClientRatingPanel";
import { formatScore, levelOf } from "../../../components/rating/ratingLevel";
import { ModalTable, RowAction } from '../components/ReportLayout';

interface ModalClientHistoryProps {
  open: boolean;
  showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
  showLoading: () => void;
  hideLoading: () => void;
  onClose: () => void;
  canManageBackupAlert?: boolean;
  onBackupAlertChange?: (clientId: number, active: boolean) => void;
  clientData?: {
    id?: number;
    loanId?: number;
    cashBoxId?: number;
    date?: string;
    routeName?: string;
    routeId?: number;
    typeOperation?: number;
    cedula?: string;
    nombre?: string;
    direccion?: string;
    telefono?: string;
    oficio?: string;
    calificacion?: string;
    celular?: string;
    backupAlertActive?: boolean;
    backups?: Array<{ id: number }>;
  } | null;
}

interface ClientInfo {
  id: number;
  idNumber: string;
  fullName: string;
  phone: string;
  mobilePhone: string;
  mainAddress: string;
  job: string;
  route?: {
    id: number;
    name: string;
  };
  backupAlertActive?: boolean;
  backups?: Array<{ id: number }>;
  loans: Array<{
    id: number;
    date: string;
    statusDate?: string | null;
    value: number;
    totalValue?: number;
    balance: number;
    daysLate: number;
    state: number;
    /** Valor en productos (sedes de objeto real). */
    productValue?: number | null;
  }>;
}

const ClientHistoryModal: React.FC<ModalClientHistoryProps> = ({
  open,
  onClose,
  clientData,
  showAlert,
  showLoading,
  hideLoading,
  canManageBackupAlert = false,
  onBackupAlertChange,
}) => {
  const t = useT();
  const [movement, setMovement] = useState<any[]>([]);
  const [clientInfo, setClientInfo] = useState<ClientInfo | null>(null);
  const [payModalOpen, setPayModalOpen] = useState(false);
  // Productos del préstamo (sedes de objeto real): entregados, devueltos y cambios.
  const [productsLoanId, setProductsLoanId] = useState<number | null>(null);
  const [productItems, setProductItems] = useState<any[]>([]);
  const [productReturns, setProductReturns] = useState<any[]>([]);
  const productsLoading = useModalLoading();

  const openProducts = async (loanId: number) => {
    setProductsLoanId(loanId);
    setProductItems([]);
    setProductReturns([]);
    productsLoading.showLoading();
    try {
      const [{ data: loan }, returns] = await Promise.all([
        axios.get(`/loans/${loanId}`),
        // Las devoluciones requieren permiso de Inventario; si no lo tiene, solo se ven los productos.
        axios.get("/product-returns", { params: { loanId } }).then((r) => r.data?.data ?? []).catch(() => []),
      ]);
      setProductItems(Array.isArray(loan?.items) ? loan.items : []);
      setProductReturns(Array.isArray(returns) ? returns : []);
    } catch (error) {
      showAlert(t("clientReports.productsLoadError"), "error");
      setProductsLoanId(null);
    } finally {
      productsLoading.hideLoading();
    }
  };
  const [selectedLoanId, setSelectedLoanId] = useState<number | null>(null);
  const [payRows, setPayRows] = useState<any[]>([]);
  const [contentLoading, setContentLoading] = useState(false);
  const [payLoading, setPayLoading] = useState(false);
  const [inputs, setInputs] = useState({
    cedula: "",
    nombre: "",
    direccion: "",
    telefono: "",
    oficio: "",
    calificacion: "",
    celular: "",
  });

  const [rows, setRows] = useState<any[]>([]);
  const [backupAlertActive, setBackupAlertActive] = useState(false);
  const [ratingScore, setRatingScore] = useState<number | null>(null);
  const ratingLevel = levelOf(ratingScore);
  const ratingLabel = ratingLevel === "none" ? t("rating.level.none") : `${formatScore(ratingScore)} · ${t(`rating.level.${ratingLevel}` as TextKey)}`;

  useEffect(() => {
    if (open && clientData) {
      // Actualizar los inputs si hay datos del cliente
      if (clientData) {
        setInputs({
          cedula: clientData.cedula || "",
          nombre: clientData.nombre || "",
          direccion: clientData.direccion || "",
          telefono: clientData.telefono || "",
          oficio: clientData.oficio || "",
          calificacion: clientData.calificacion || "",
          celular: clientData.celular || "",
        });
        setBackupAlertActive(Boolean(clientData.backupAlertActive));
      }

      const fetchClientHistory = async () => {
        if (!clientData.cedula) {
          return;
        }
        try {
          setContentLoading(true);
          const formData: any = {
            idNumber: clientData.cedula,
          };
          if (clientData.routeId) {
            formData.routeId = clientData.routeId;
          }
          const { data } = await axios.post("/clients/lookup", formData);
          if (data?.data?.length > 0) {
            const clients = data.data as ClientInfo[];
            const info = clients[0];
            const selectedLoanId = Number(clientData?.loanId || 0);
            const loans = Array.isArray(info.loans) ? info.loans : [];
            const filteredLoans =
              selectedLoanId > 0
                ? loans.filter((loan) => Number(loan.id) === selectedLoanId)
                : loans;
            setClientInfo(info);
            setInputs({
              cedula: info.idNumber || "",
              nombre: info.fullName || "",
              direccion: info.mainAddress || "",
              telefono: info.phone || "",
              oficio: info.job || "",
              calificacion: "",
              celular: info.mobilePhone || "",
            });
            setBackupAlertActive(Boolean(info.backupAlertActive));
            const mappedRows = clients.flatMap((client) => {
              const clientLoans = Number(clientData?.loanId || 0) > 0
                ? (client.loans || []).filter((loan) => Number(loan.id) === Number(clientData?.loanId || 0))
                : (client.loans || []);

              return clientLoans.map((loan) => ({
                id: loan.id,
                date: loan.date ? moment(loan.date).format("YYYY/MM/DD") : "",
                statusDate: loan.statusDate ? moment(loan.statusDate).format("YYYY/MM/DD") : "",
                value: loan.totalValue ?? loan.value ?? 0,
                balance: loan.balance ?? 0,
                daysLate: loan.daysLate ?? 0,
                active: loan.state === 0,
                state: loan.state === 0 ? t("clientReports.loanActive") : t("clientReports.loanPaidOff"),
                productValue: Number(loan.productValue ?? 0),
                route: clientData.routeName || client.route?.name || "",
                actions: "",
              }));
            });
            setRows(mappedRows);

            if (selectedLoanId > 0 && filteredLoans.length === 0) {
              showAlert(t("clientReports.noHistoryForLoan"), "info");
            }
          } else {
            setClientInfo(null);
            setRows([]);
            showAlert(t("clientReports.noClientData"), "info");
          }
        } catch (error) {
          console.error("Error al obtener historial del cliente:", error);
          showAlert(t("clientReports.clientLoadError"), "error");
        } finally {
          setContentLoading(false);
        }
      };

      const fetchData = async () => {
        try {
          if (clientData.cashBoxId && clientData.date && clientData.routeName && clientData.typeOperation !== undefined) {
            const formData = {
              cashBoxId: clientData.cashBoxId,
              date: clientData.date,
              route: clientData.routeName,
              routeId: clientData.routeId,
              typeOperation: clientData.typeOperation,
            };
            const { data } = await axios.post("/reports/expenses/detail", formData);
            setMovement(data.data);
          }
        } catch (error) {
          console.error("Error al cargar datos del modal:", error);
        }
      };

      fetchClientHistory();
      fetchData();
    }
  }, [open, clientData, showAlert, t]);


  const handleOpenPayDetail = async (loanId: number) => {
    setSelectedLoanId(loanId);
    setPayModalOpen(true);
    try {
      setPayLoading(true);
      const { data } = await axios.get(`/loans/${loanId}/payments`);
      const mappedPayments = (data || []).map((payment: any) => {
        const baseDate = payment.paymentDate || payment.operationDate;
        const opDate = payment.operationDate || payment.paymentDate;
        return {
          id: payment.id,
          date: baseDate ? moment(baseDate).format("YYYY/MM/DD") : "",
          hour: opDate ? moment(opDate).format("HH:mm") : "",
          value: payment.value ?? 0,
          discountValue: payment.discountValue ?? 0,
          balance: payment.balance ?? 0,
        };
      });
      setPayRows(mappedPayments);
    } catch (error) {
      console.error("Error al obtener detalle de abonos:", error);
      showAlert(t("clientReports.paymentsLoadError"), "error");
    } finally {
      setPayLoading(false);
    }
  };

  const handleClosePayDetail = () => {
    setPayModalOpen(false);
    setSelectedLoanId(null);
    setPayRows([]);
  };

  const handleToggleBackupAlert = async () => {
    const clientId = Number(clientInfo?.id || clientData?.id || 0);
    if (!Number.isFinite(clientId) || clientId <= 0) {
      showAlert(t("clientReports.invalidClientAlert"), "warning");
      return;
    }

    const nextActive = !backupAlertActive;

    try {
      setContentLoading(true);
      const { data } = await axios.put(`/clients/${clientId}/backup-alert`, { active: nextActive });
      const refreshedClient = data?.client;
      const finalActive = Boolean(refreshedClient?.backupAlertActive ?? nextActive);
      setBackupAlertActive(finalActive);
      setClientInfo((current) =>
        current
          ? {
              ...current,
              backupAlertActive: finalActive,
            }
          : current
      );
      onBackupAlertChange?.(clientId, finalActive);
      showAlert(finalActive ? t("clientReports.alertOn") : t("clientReports.alertOff"), "success");
    } catch (error) {
      console.error("Error al actualizar la alerta del cliente:", error);
      showAlert(t("clientReports.alertUpdateError"), "error");
    } finally {
      setContentLoading(false);
    }
  };

  const hasBackups = (clientInfo?.backups?.length ?? clientData?.backups?.length ?? 0) > 0;

  // Historial de préstamos del cliente
  const historialColumns: DataTableColumn<any>[] = [
    { id: "id", header: t("clientReports.loanNumberCol"), value: (r) => r.id, sortable: true },
    {
      id: "state",
      header: t("common.status"),
      value: (r) => r.state,
      render: (r) => <StatusChip label={r.state} tone={r.active ? "info" : "neutral"} />,
      sortable: true,
    },
    { id: "date", header: t("common.date"), value: (r) => r.date, sortable: true },
    { id: "statusDate", header: t("clientReports.paidOffDate"), value: (r) => r.statusDate, hideBelow: "sm" },
    { id: "value", header: t("clientReports.value"), value: (r) => Number(r.value ?? 0), render: (r) => formatCurrency(Number(r.value ?? 0)), align: "right", sortable: true },
    { id: "route", header: t("summaryReports.shared.route"), value: (r) => r.route, hideBelow: "sm" },
  ];

  // Movimientos de caja (cuando se abre desde un resumen con caja)
  const movementRows = movement.map((data, index) => ({
    ...data,
    id: data.id || `mov-${index}`,
    date: moment(data.date).format("YYYY/MM/DD") || "",
    hour: moment(data.date).format("HH:mm") || "",
    value: data.value || "",
    description: data.description || "",
  }));
  const movementColumns: DataTableColumn<any>[] = [
    { id: "date", header: t("common.date"), value: (r) => r.date, sortable: true },
    { id: "hour", header: t("clientReports.time"), value: (r) => r.hour },
    { id: "value", header: t("clientReports.value"), value: (r) => r.value, align: "right" },
    { id: "description", header: t("clientReports.description"), value: (r) => r.description, wrap: true },
    { id: "type", header: t("clientReports.type"), value: (r) => r.type ?? "" },
  ];

  const info = (label: string, value: React.ReactNode) => (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      <Typography component="div" variant="body2" fontWeight={500} sx={{ overflowWrap: "anywhere" }}>
        {value === "" || value === null || value === undefined ? "—" : value}
      </Typography>
    </Box>
  );

  const clientFields = (
    <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(150px, 100%), 1fr))", gap: 2 }}>
      {info(t("common.idNumber"), inputs.cedula)}
      {info(t("common.name"), inputs.nombre)}
      {info(t("common.address"), inputs.direccion)}
      {info(t("common.phone"), inputs.telefono)}
      {info(t("common.cellphone"), inputs.celular)}
      {info(t("clientReports.job"), inputs.oficio)}
      {info(
        t("clientReports.rating"),
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          {ratingScore !== null && <Rating value={ratingScore} precision={0.1} readOnly size="small" />}
          <span>{ratingLabel}</span>
        </Box>,
      )}
    </Box>
  );

  const sectionTitle = (text: React.ReactNode, extra?: React.ReactNode) => (
    <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1, mb: 1 }}>
      <Typography variant="subtitle1" component="h3" sx={{ fontWeight: 600 }}>
        {text}
      </Typography>
      {extra}
    </Box>
  );

  return (
    <>
      <StandardModal
        open={open}
        onClose={onClose}
        title={clientData?.cashBoxId ? t("clientReports.expensesInfoTitle") : t("clientReports.clientInfoTitle")}
        subtitle={inputs.nombre || undefined}
        maxWidth={900}
        loading={contentLoading}
        composed
      >
        <StandardModalBody>
          {contentLoading ? null : clientData?.cashBoxId ? (
            <Box sx={{ display: "grid", gap: 3, minWidth: 0 }}>
              {clientFields}
              <Box sx={{ minWidth: 0 }}>
                {sectionTitle(
                  t("clientReports.movementsTitle", {
                    route: clientData?.typeOperation === 3 ? t("clientReports.wholeRoute") : clientData?.routeName || t("summaryReports.shared.route"),
                  }),
                  <Typography variant="body2" color="text.secondary">
                    {t("clientReports.dateLabel")} {clientData?.date || ""}
                  </Typography>,
                )}
                <ModalTable
                  rows={movementRows}
                  columns={movementColumns}
                  getRowId={(r) => r.id}
                  exportFileName={t("clientReports.movementsFileName", { date: nowOperationalDateString() })}
                  maxHeight="min(40vh, 360px)"
                  ariaLabel={t("clientReports.expensesInfoTitle")}
                />
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: "grid", gap: 3, minWidth: 0 }}>
              {clientFields}
              <Box sx={{ minWidth: 0 }}>
                {sectionTitle(
                  t("clientReports.loansHistory"),
                  hasBackups ? (
                    <PermissionButton
                      allowed={canManageBackupAlert}
                      deniedTooltip={t("clientReports.noPermissionAlert")}
                      size="small"
                      variant={backupAlertActive ? "contained" : "outlined"}
                      color={backupAlertActive ? "warning" : "inherit"}
                      startIcon={backupAlertActive ? <NotificationsActiveOutlined /> : <NotificationsOffOutlined />}
                      onClick={handleToggleBackupAlert}
                    >
                      {backupAlertActive ? t("clientReports.disableAlert") : t("clientReports.enableAlert")}
                    </PermissionButton>
                  ) : null,
                )}
                <ModalTable
                  rows={rows}
                  columns={historialColumns}
                  getRowId={(r) => r.id}
                  searchable={false}
                  initialPageSize={10}
                  pageSizeOptions={[10, 25, 50]}
                  maxHeight="min(40vh, 360px)"
                  ariaLabel={t("clientReports.loansHistory")}
                  emptyState={<EmptyState dense title={t("clientReports.noClientData")} />}
                  rowActions={(row) => (
                    <>
                      <RowAction
                        label={t("clientReports.paymentsDetailTitle")}
                        icon={<ReceiptLongOutlined fontSize="small" />}
                        onClick={() => handleOpenPayDetail(Number(row.id))}
                      />
                      {Number(row.productValue || 0) > 0 && (
                        <RowAction
                          label={t("clientReports.products")}
                          icon={<Inventory2Outlined fontSize="small" />}
                          onClick={() => openProducts(Number(row.id))}
                        />
                      )}
                    </>
                  )}
                />
              </Box>
              {clientInfo?.id ? (
                <ClientRatingPanel
                  clientId={clientInfo.id}
                  clientName={clientInfo.fullName}
                  showAlert={showAlert}
                  onLoaded={(r) => setRatingScore(r.score)}
                />
              ) : null}
            </Box>
          )}
        </StandardModalBody>
        <StandardModalFooter>
          <span />
          <Button variant="outlined" onClick={onClose}>
            {t("common.close")}
          </Button>
        </StandardModalFooter>
      </StandardModal>
      <StandardModal
        open={payModalOpen}
        onClose={handleClosePayDetail}
        title={t("clientReports.paymentsDetailTitle")}
        subtitle={selectedLoanId ? t("clientReports.loanNumber", { id: selectedLoanId }) : undefined}
        maxWidth={760}
        loading={payLoading}
        actions={
          <>
            <span />
            <Button variant="outlined" onClick={handleClosePayDetail}>
              {t("common.close")}
            </Button>
          </>
        }
      >
        {payLoading ? null : (
          <ModalTable
            rows={payRows}
            getRowId={(r: any) => r.id}
            searchable={false}
            ariaLabel={t("clientReports.paymentsDetailTitle")}
            columns={[
              { id: "date", header: t("common.date"), value: (r: any) => r.date, sortable: true },
              { id: "hour", header: t("clientReports.time"), value: (r: any) => r.hour },
              { id: "value", header: t("clientReports.value"), value: (r: any) => Number(r.value ?? 0), render: (r: any) => formatCurrency(Number(r.value ?? 0)), align: "right" },
              { id: "discountValue", header: t("clientReports.discount"), value: (r: any) => Number(r.discountValue ?? 0), render: (r: any) => formatCurrency(Number(r.discountValue ?? 0)), align: "right" },
              { id: "balance", header: t("clientReports.balance"), value: (r: any) => Number(r.balance ?? 0), render: (r: any) => formatCurrency(Number(r.balance ?? 0)), align: "right" },
            ]}
          />
        )}
      </StandardModal>
      <StandardModal
        open={productsLoanId !== null}
        onClose={() => setProductsLoanId(null)}
        title={t("clientReports.loanProductsTitle", { id: productsLoanId ?? "" })}
        subtitle={inputs.nombre || undefined}
        maxWidth={900}
        loading={productsLoading.loading}
        actions={
          <>
            <span />
            <Button variant="outlined" onClick={() => setProductsLoanId(null)}>
              {t("common.close")}
            </Button>
          </>
        }
      >
        <LoanProductsHistoryModal items={productItems} returns={productReturns} />
      </StandardModal>
    </>
  );
};

export default ClientHistoryModal;
